import React, { useEffect, useCallback, useRef, useState } from 'react';
import { customGaEvent, PublicGaEvents } from '../../../../../utils/ga';
import { usePrevious } from '../../../../../hooks';
import AframeButton from '../../../components/AframeButton';

const audioWidth = 2.2;

const InWorldAudio = props => {
  const {
    darkMode,
    iconId,
    id,
    src,
    toggleMainMediaStatus,
    tourId,
    tourName,
    title,
    isMainMediaPlaying,
    setShowModal,
  } = props;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [played, setPlayed] = useState(false);

  const [soundLoaded, setSoundLoaded] = useState(0);
  const [duration, setDuration] = useState(0);

  const wasPlaying = usePrevious(isPlaying);
  const wasMuted = usePrevious(isMuted);
  const prevSoundLoaded = usePrevious(soundLoaded);

  const playButtonRef = useRef();
  const muteButtonRef = useRef();
  const transcriptButtonRef = useRef();
  const planeRef = useRef();
  const soundRef = useRef();
  const progressBarRef = useRef();

  const handleTranscript = useCallback(() => {
    setShowModal(true);
    // Google Analytics tracking
    customGaEvent({
      action: PublicGaEvents.HOTSPOT_AUDIO_TRANSCRIPT,
      label: title,
    });
  }, [setShowModal, title]);

  const handlePlay = useCallback(() => {
    setIsPlaying(!isPlaying);
    // pause main tour audio when hotspot audio is played
    toggleMainMediaStatus(false);
    setPlayed(true);
    if (!isPlaying) {
      // Google Analytics tracking
      customGaEvent({ action: PublicGaEvents.HOTSPOT_AUDIO_PLAY, label: title });
    }
  }, [isPlaying, setPlayed, title, toggleMainMediaStatus, setIsPlaying]);

  useEffect(() => {
    const sound = soundRef.current;
    const progress = progressBarRef.current;
    const playButton = playButtonRef.current;
    const muteButton = muteButtonRef.current;
    const transcriptButton = transcriptButtonRef.current;

    // pause hotspot audio when main tour audio is played
    if (isPlaying && isMainMediaPlaying && wasPlaying) {
      setIsPlaying(false);
    }

    const handleMute = () => {
      setIsMuted(!isMuted);
      if (!isMuted) {
        // Google Analytics tracking
        customGaEvent({ action: PublicGaEvents.HOTSPOT_AUDIO_MUTE, label: title });
      }
    };

    const handleSetSoundLoaded = e => {
      setSoundLoaded(true);
    };

    const handleSoundFinished = () => {
      setIsPlaying(false);
      progress.emit('sound-ended');
    };

    if (!prevSoundLoaded && soundLoaded) {
      setDuration(
        sound.components.sound.pool.children[0].buffer.duration * 1000
      );
    }

    if (wasPlaying && !isPlaying) {
      sound.components.sound.pauseSound();
      progress.emit('pauseprogress');
    }

    if (!wasPlaying && isPlaying) {
      if (soundLoaded && sound && sound.components && sound.components.sound) {
        sound.components.sound.playSound();
        progress.emit(played ? 'resumeprogress' : 'startprogress');
      } else {
        setIsPlaying(false);
      }
    }

    if (wasMuted && !isMuted) {
      sound && sound.setAttribute('volume', 1);
    }

    if (!wasMuted && isMuted) {
      sound && sound.setAttribute('volume', 0);
    }

    sound && sound.addEventListener('sound-loaded', handleSetSoundLoaded);

    sound && sound.addEventListener('sound-ended', handleSoundFinished);

    playButton && playButton.addEventListener('click', handlePlay);

    muteButton && muteButton.addEventListener('click', handleMute);

    transcriptButton &&
      transcriptButton.addEventListener('click', handleTranscript);

    return () => {
      sound && sound.removeEventListener('sound-loaded', handleSetSoundLoaded);

      sound && sound.removeEventListener('sound-ended', handleSoundFinished);

      playButton && playButton.removeEventListener('click', handlePlay);

      muteButton && muteButton.removeEventListener('click', handleMute);

      transcriptButton &&
        transcriptButton.removeEventListener('click', handleTranscript);
    };
  }, [
    handlePlay,
    handleTranscript,
    isMainMediaPlaying,
    isMuted,
    isPlaying,
    played,
    prevSoundLoaded,
    soundLoaded,
    title,
    tourId,
    tourName,
    wasMuted,
    wasPlaying,
  ]);

  const playIconSrc = !darkMode
    ? isPlaying
      ? '#hotspot__pause-icon-black'
      : `#hotspot__play-icon-black`
    : isPlaying
    ? `#hotspot__pause-icon`
    : `#hotspot__play-icon`;

  const muteIconSrc = !darkMode
    ? isMuted
      ? '#hotspot__volume-off-icon-black'
      : `#hotspot__volume-on-icon-black`
    : isMuted
    ? `#hotspot__volume-off-icon`
    : `#hotspot__volume-on-icon`;

  return (
    <a-plane
      id={id}
      visible="false"
      width={audioWidth}
      height="0.75"
      scale="1.5 1.5 1.5"
      position="0 1.6 -0.4"
      ref={planeRef}
    >
      <a-sound
        ref={soundRef}
        refDistance="0.1"
        loop="false"
        src={`src: url(${src})`}
        volume={isMuted ? 0 : 1}
      />
      {/* Background */}
      <a-plane
        color={darkMode ? '#000' : '#fff'}
        material="transparent: true; depthWrite: true;"
        opacity="0.7"
        width={audioWidth}
        height="0.75"
        scale="1 1 1"
        position="0 0 0"
      />
      {/* Progress Bar */}
      <a-plane
        position="0 -0.4 0"
        width={audioWidth}
        height="0.05"
        scale="1 1 1"
        geometry="face: front;"
        material="color: #009c6b; shader: flat; transparent: true; depthWrite: false"
      />
      <a-plane
        ref={progressBarRef}
        position="0 -0.4 0"
        width={audioWidth}
        height="0.05"
        geometry="face: front;"
        material={`color: ${!darkMode ? '#000' : '#fff'}; transparent: true;`}
        animation={`property: position; from: 0 -0.4 0; to: ${audioWidth /
          2} -0.4 0; loop: false; startEvents: startprogress,sound-ended; resumeEvents: resumeprogress; pauseEvents: pauseprogress; dur: ${duration}`}
        animation__width={`property: scale; from: 1 1 1; to: 0 1 1; loop: false; startEvents: startprogress,sound-ended; resumeEvents: resumeprogress; pauseEvents: pauseprogress; dur: ${duration}`}
      />
      {/* Audio Buttons */}
      <AframeButton
        id={`${iconId}-play-toggle`}
        forwardRef={playButtonRef}
        src={playIconSrc}
        position="-0.75 0 0.01"
      />
      <AframeButton
        id={`${iconId}-audio-toggle`}
        forwardRef={muteButtonRef}
        src={muteIconSrc}
        position="0 0 0.01"
      />
      <AframeButton
        id={`${iconId}-audio-toggle`}
        forwardRef={transcriptButtonRef}
        src={
          !darkMode
            ? '#hotspot__description-icon-black'
            : '#hotspot__description-icon'
        }
        position="0.75 0 0.01"
      />
    </a-plane>
  );
};

export default InWorldAudio;
